import React, { useState, useEffect, useRef } from 'react';
import './CommunicativeSituationsExercise.css';
import { fetchOpenAIText } from '../../utils/openaiClient';

const ROLEPLAY_SECTIONS = [
  { key: 'SITUATION',      icon: '📍', label: 'Situation' },
  { key: 'ROLES',          icon: '👥', label: 'Roles' },
  { key: 'OBJECTIVE',      icon: '🎯', label: 'Objective' },
  { key: 'USE THESE WORDS',icon: '📚', label: 'Use these words' },
];

const parseRoleplay = (text) => {
  const result = [];
  for (let i = 0; i < ROLEPLAY_SECTIONS.length; i++) {
    const { key, icon, label } = ROLEPLAY_SECTIONS[i];
    const rx = new RegExp(`${key}:\\s*`, 'i');
    const m = text.match(rx);
    if (!m) continue;
    const start = m.index + m[0].length;
    let end = text.length;
    for (let j = i + 1; j < ROLEPLAY_SECTIONS.length; j++) {
      const nm = text.slice(start).match(new RegExp(`${ROLEPLAY_SECTIONS[j].key}:`, 'i'));
      if (nm) { end = start + nm.index; break; }
    }
    result.push({ key, icon, label, content: text.slice(start, end).trim() });
  }
  return result.length > 0 ? result : [{ key: 'TEXT', icon: '🎭', label: 'Role Play', content: text }];
};

const CsRolePlayCard = ({ text, openIdx, onToggle }) => {
  const sections = parseRoleplay(text);
  return (
    <div className="cs-rp-card">
      <div className="cs-rp-header">
        <span className="cs-rp-header-icon">🎭</span>
        <span className="cs-rp-header-title">Role Play</span>
      </div>
      {sections.map((sec, si) => {
        const isOpen = openIdx === si;
        return (
          <div key={si} className={`cs-rp-section${si < sections.length - 1 ? ' has-border' : ''}`}>
            <button
              type="button"
              className="cs-rp-section-toggle"
              onClick={() => onToggle(isOpen ? null : si)}
            >
              <span className="cs-rp-section-icon">{sec.icon}</span>
              <span className="cs-rp-section-label">{sec.label}</span>
              <svg
                className={`cs-collapsible-arrow${isOpen ? ' is-open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isOpen && (
              <div className="cs-rp-section-content">
                {sec.key === 'USE THESE WORDS' ? (
                  <div className="cs-rp-words">
                    {sec.content.split(/[,;]+/).map(w => w.replace(/\*\*/g, '').trim()).filter(Boolean).map((word, wi) => (
                      <span key={wi} className="cs-rp-word">{word}</span>
                    ))}
                  </div>
                ) : (
                  <p
                    className="cs-rp-text"
                    dangerouslySetInnerHTML={{ __html: sec.content.replace(/\*\*(.*?)\*\*/g, '<strong class="cs-rp-highlight">$1</strong>') }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const palette = [
  { avatar: '#ee5a24', bubble: '#fff4ed', border: '#fed7aa' },
  { avatar: '#3b82f6', bubble: '#eff6ff', border: '#bfdbfe' },
  { avatar: '#10b981', bubble: '#ecfdf5', border: '#a7f3d0' },
  { avatar: '#8b5cf6', bubble: '#f5f3ff', border: '#ddd6fe' },
  { avatar: '#ec4899', bubble: '#fdf2f8', border: '#fbcfe8' },
  { avatar: '#14b8a6', bubble: '#ecfeff', border: '#a5f3fc' },
];

const CommunicativeSituationsExercise = ({
  title = 'Communicative Situation',
  level = 'B1',
  topic = '',
  vocab = '',
  situations = [],
  readOnly: _readOnly = false,
  onClose,
  updateAttributes,
  node: _node,
  editor: _editor,
  getPos: _getPos,
  isStudent = false,
  myState: _myState = {},
  otherState = {},
  updateState = () => {},
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [editableSituations, setEditableSituations] = useState([...situations]);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const [editableTopic, setEditableTopic] = useState(topic);
  const [editableVocab, setEditableVocab] = useState(vocab);
  const [isExtending, setIsExtending] = useState(false);
  const [rpOpen, setRpOpen] = useState({});

  useEffect(() => {
    if (otherState.rpOpen && typeof otherState.rpOpen === 'object') {
      setRpOpen(otherState.rpOpen);
    }
  }, [otherState.rpOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const [inferredTopic, setInferredTopic] = useState('');
  const inferredRef = useRef(false);

  useEffect(() => {
    if (editableTopic || !editableVocab || inferredRef.current) return;
    inferredRef.current = true;
    fetchOpenAIText({
      prompt: `These vocabulary words are used in a dialogue: "${editableVocab}". In 2-5 words, what is the general topic of this conversation? Reply with only the topic, no punctuation.`,
      temperature: 0.3,
      maxTokens: 20,
    }).then(result => {
      if (result) setInferredTopic(result.trim().replace(/[.!?,;]+$/, ''));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        situations: editableSituations,
        title: editableTitle,
        level: editableLevel,
        topic: editableTopic,
        vocab: editableVocab,
      });
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableLevel(level);
    setEditableTopic(topic);
    setEditableVocab(vocab);
    setEditableSituations([...situations]);
  };

  const handleExtendDialogue = async () => {
    if (!editableVocab.trim()) return;
    setIsExtending(true);

    try {
      const dialogueIndex = editableSituations.findIndex(item => item.type === 'dialogue');
      if (dialogueIndex === -1) return;

      const currentDialogue = editableSituations[dialogueIndex];
      const cleanHtml = currentDialogue.html ? currentDialogue.html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '') : '';

      const prompt = `Continue this dialogue naturally. Keep the same characters and context. Use the target vocabulary: ${editableVocab}

Current dialogue:
${cleanHtml}

Continue with 4-6 more lines. Format as:
Role1: [line with **target vocab**]
Role2: [response with **target vocab**]
Role1: [line with **target vocab**]
Role2: [response with **target vocab**]
...

LEVEL REQUIREMENTS (${editableLevel}):
- A1: Use only basic present tense, simple vocabulary, short sentences (5-8 words), common everyday topics
- A2: Use present/past simple, basic future (will), common phrases, sentences up to 12 words
- B1: Use present/past/future tenses, conditionals, some complex sentences (up to 15 words), intermediate topics
- B2: Use all tenses, passive voice, complex sentences (up to 20 words), abstract concepts
- C1: Use advanced grammar, idiomatic expressions, complex structures, sophisticated vocabulary
- C2: Use native-level complexity, subtle nuances, advanced idiomatic language

IMPORTANT:
- Use the target vocabulary words in **bold** format
- Strictly follow the ${editableLevel} level requirements
- Make dialogue natural and engaging for the specified level
- Ensure vocabulary difficulty matches the level`;

      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 500,
      });

      if (content) {
        const extendedDialogue = currentDialogue.html + '<br><br>' + content.replace(/\n/g, '<br>');

        const newSituations = [...editableSituations];
        newSituations[dialogueIndex] = { ...currentDialogue, html: extendedDialogue };
        setEditableSituations(newSituations);

        updateAttributes({
          situations: newSituations,
          topic: editableTopic,
          vocab: editableVocab,
          level: editableLevel
        });
      }
    } catch (error) {
      console.error('Error extending dialogue:', error);
    } finally {
      setIsExtending(false);
    }
  };

  return (
    <div className="cs-exercise">
      <div className="cs-header">
        <div className="cs-header-left">
          <div className="cs-header-icon">
            <svg viewBox="0 0 48 48" width="18" height="18" fill="white" xmlns="http://www.w3.org/2000/svg">
              <g id="Chat">
                <path d="m45 1h-28a2 2 0 0 0 -2 2v20a2 2 0 0 0 2 2h18s0 0 8 4v-4h2a2 2 0 0 0 2-2v-20a2 2 0 0 0 -2-2zm-23 14a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm9 0a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm9 0a2 2 0 1 1 2-2 2 2 0 0 1 -2 2z"/>
                <path d="m13 23v-4h-10a2 2 0 0 0 -2 2v20a2 2 0 0 0 2 2h2v4c8.08-4 7.85-4 8-4h18.05a2 2 0 0 0 2-2v-14h-16.05a4 4 0 0 1 -4-4zm-5 10a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm18-4a2 2 0 1 1 -2 2 2 2 0 0 1 2-2zm-7 2a2 2 0 1 1 -2-2 2 2 0 0 1 2 2z"/>
              </g>
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              className="cs-title-input"
              placeholder="Title"
            />
          ) : (
            <span className="cs-title">{editableTitle}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={e => setEditableLevel(e.target.value)}
              className="cs-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="cs-level-badge">{editableLevel}</span>
          )}
        </div>
        <div className="cs-header-actions">
          {!isEditing && !isStudent && (
            <button className="cs-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="cs-icon-btn" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="cs-icon-btn" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {!isEditing && onClose && (
            <button className="cs-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="cs-body">
        {!isStudent && (
          <div className="cs-collapsible">
            <div
              className="cs-collapsible-header"
              onClick={() => setShowInstructions(!showInstructions)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowInstructions(!showInstructions)}
            >
              <span className="cs-collapsible-title">How to use this exercise</span>
              <svg
                className={`cs-collapsible-arrow${showInstructions ? ' is-open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </div>
            {showInstructions && (
              <div className="cs-collapsible-content">
                <ul className="cs-instructions-list">
                  <li>Read through the dialogue together to set the scene</li>
                  <li>Open each Role Play card to reveal the situation, roles, objective and target words</li>
                  <li>Have students act out the roles, using the target vocabulary</li>
                  <li>Use "Continue Dialogue" to generate more lines with the same characters</li>
                  <li>Encourage students to swap roles and improvise their own responses</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {isEditing && (
          <div className="cs-field-row">
            <input
              value={editableTopic}
              onChange={e => setEditableTopic(e.target.value)}
              className="cs-topic-input"
              placeholder="Topic"
            />
            <input
              value={editableVocab}
              onChange={e => setEditableVocab(e.target.value)}
              className="cs-topic-input"
              placeholder="Target vocabulary (comma-separated)"
            />
          </div>
        )}

        {editableSituations.map((item, idx) => (
          <div key={idx} className="cs-situation">
            {item.type === 'dialogue' ? (
              <>
                {(() => {
                  const cleanHtml = item.html ? item.html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '') : '';
                  const lines = cleanHtml.split('\n').filter(line => line.trim());
                  const roles = new Set();
                  lines.forEach(line => {
                    const m = line.match(/^([^:]+):/);
                    if (m) roles.add(m[1].trim());
                  });
                  const roleArray = Array.from(roles);

                  let situationDescription = '';
                  if (roleArray.length >= 2) {
                    situationDescription = `${roleArray.slice(0, -1).join(', ')} and ${roleArray[roleArray.length - 1]} are having a conversation`;
                  } else if (roleArray.length === 1) {
                    situationDescription = `${roleArray[0]} is speaking`;
                  } else {
                    situationDescription = 'A conversation is taking place';
                  }

                  const topicDescription = editableTopic
                    ? ` about ${editableTopic}`
                    : inferredTopic
                      ? ` about ${inferredTopic}`
                      : '';

                  return (
                    <div className="cs-callout">
                      {situationDescription}{topicDescription}
                    </div>
                  );
                })()}

                <div className="cs-bubbles">
                  {item.html && (() => {
                    const cleanHtml = item.html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '');
                    const lines = cleanHtml.split('\n').filter(line => line.trim());
                    const nameToIndex = new Map();

                    return lines.map((line, lineIndex) => {
                      if (!line.trim()) return null;

                      const roleMatch = line.match(/^([^:]+):\s*(.*)$/);
                      if (!roleMatch) return null;

                      const [, role, message] = roleMatch;
                      const cleanRole = role.trim();
                      const cleanMessage = message.trim();
                      const isRight = lineIndex % 2 === 1;

                      const displayRole = cleanRole.replace(/\b(student|you|learner|i|me)\b/gi, (match) => {
                        const lower = match.toLowerCase();
                        if (lower === 'student' || lower === 'learner') return 'Student';
                        if (lower === 'you' || lower === 'i' || lower === 'me') return 'You';
                        return match;
                      });

                      const initials = displayRole.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
                      let assigned = nameToIndex.get(displayRole);
                      if (assigned === undefined) {
                        assigned = nameToIndex.size % palette.length;
                        nameToIndex.set(displayRole, assigned);
                      }
                      const scheme = palette[assigned];

                      return (
                        <div key={lineIndex} className={`cs-bubble-row${isRight ? ' right' : ''}`}>
                          {!isRight && <div className="cs-bubble-avatar" style={{ background: scheme.avatar }}>{initials}</div>}
                          <div className={`cs-bubble${isRight ? ' right' : ''}`} style={{ background: scheme.bubble, borderColor: scheme.border }}>
                            <div className="cs-bubble-name">{displayRole}</div>
                            <p
                              className="cs-bubble-text"
                              dangerouslySetInnerHTML={{
                                __html: cleanMessage.replace(/\*\*(.*?)\*\*/g, '<strong class="cs-vocab-highlight">$1</strong>')
                              }}
                            />
                          </div>
                          {isRight && <div className="cs-bubble-avatar" style={{ background: scheme.avatar }}>{initials}</div>}
                        </div>
                      );
                    }).filter(Boolean);
                  })()}
                </div>

                {isEditing && (
                  <div className="cs-continue-row">
                    <button
                      type="button"
                      onClick={handleExtendDialogue}
                      className="cs-continue-btn"
                      disabled={isExtending}
                    >
                      {isExtending ? 'Extending…' : 'Continue Dialogue'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <CsRolePlayCard
                text={item.text}
                openIdx={rpOpen[idx] ?? null}
                onToggle={(si) => setRpOpen(prev => {
                  const next = { ...prev, [idx]: prev[idx] === si ? null : si };
                  updateState({ rpOpen: next });
                  return next;
                })}
              />
            )}
          </div>
        ))}
      </div>

      {isEditing && (
        <div className="cs-footer">
          <button className="cs-cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="cs-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default CommunicativeSituationsExercise;
